import { Note } from '../types';
import { formatTimestamp } from './formatTimestamp';
import { toPlotlyLocal } from './plotlyLocalTime';
import { noteAuthor } from './noteAuthor';

export interface PlotlyNoteOverlayInputs {
  notes?: Note[];
  color: string;
  rangeStart?: string;
  rangeEnd?: string;
  /** FS-622: IANA zone the host chart's x-axis is drawn in. Markers have to
   *  share it or they land at the wrong point on the axis. */
  timeZone?: string | null;
}

export interface PlotlyNoteOverlay {
  shapes: Array<Record<string, unknown>>;
  trace: Record<string, unknown> | null;
}

const clipToRange = (notes: Note[], rangeStart?: string, rangeEnd?: string): Note[] => {
  if (!rangeStart && !rangeEnd) return notes;
  const startMs = rangeStart ? Date.parse(`${rangeStart}T00:00:00Z`) : -Infinity;
  // Include a full day on the end side.
  const endMs = rangeEnd ? Date.parse(`${rangeEnd}T23:59:59Z`) : Infinity;
  return notes.filter((n) => {
    const ms = Date.parse(n.created_at);
    return Number.isFinite(ms) && ms >= startMs && ms <= endMs;
  });
};

const truncateText = (text: string, max = 140): string => {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + '…';
};

const escapePlotlyText = (text: string): string =>
  text.replace(/<br>/g, ' ').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>');

/**
 * Build plotly shapes + a hoverable scatter trace that render note markers
 * above the plot area. Hover displays text + metadata; clicking the marker
 * point reports the image URL (if any) via the chart's `onClick` handler
 * — caller wires that to the ImageModal.
 */
export const buildPlotlyNoteOverlay = (
  inputs: PlotlyNoteOverlayInputs,
): PlotlyNoteOverlay => {
  const { notes = [], color, rangeStart, rangeEnd, timeZone } = inputs;
  const visible = clipToRange(notes, rangeStart, rangeEnd);
  if (visible.length === 0) return { shapes: [], trace: null };

  const shapes = visible.map((note) => ({
    type: 'line' as const,
    xref: 'x' as const,
    yref: 'paper' as const,
    x0: toPlotlyLocal(note.created_at, timeZone),
    x1: toPlotlyLocal(note.created_at, timeZone),
    y0: 0,
    y1: 1,
    line: { color, width: 1, dash: 'dot' as const },
    opacity: 0.35,
  }));

  const x = visible.map((n) => toPlotlyLocal(n.created_at, timeZone));
  const hoverlines = visible.map((n) => {
    const when = formatTimestamp(n.created_at);
    const changed = n.filter_changed ? ' [filter changed]' : '';
    const imageCount = n.images?.length ?? 0;
    const imgHint = imageCount > 0
      ? `<br><i>click marker to view ${imageCount > 1 ? `${imageCount} images` : 'image'}</i>`
      : '';
    return `<b>${when}</b>${changed}<br>${escapePlotlyText(noteAuthor(n))}<br>${escapePlotlyText(
      truncateText(n.note_text),
    )}${imgHint}`;
  });

  // customdata[0] = note id (used as the key for fetching presigned URLs
  // post-click); customdata[1] = truthy iff the note has any image attached
  // (drives the hover hint and whether the click does anything). The raw
  // image URLs are intentionally NOT embedded — Plan 67b moved attachments
  // behind an authenticated, scope-checked endpoint.
  const customdata = visible.map((n) => [n.id, (n.images?.length ?? 0) > 0 ? 1 : 0]);

  const trace: Record<string, unknown> = {
    x,
    y: Array(visible.length).fill(1),
    yaxis: 'y2',
    mode: 'markers',
    type: 'scatter',
    name: 'Notes',
    marker: {
      symbol: 'triangle-down',
      size: 12,
      color,
      line: { color: '#ffffff', width: 1 },
    },
    text: hoverlines,
    hoverinfo: 'text',
    hovertemplate: '%{text}<extra></extra>',
    customdata,
    showlegend: false,
  };

  return { shapes, trace };
};

/**
 * Extract the note id from a plotly click event on a note marker point.
 * Returns null if the click wasn't on a note marker or the note has no image
 * attached.
 */
export const noteIdFromPlotlyClick = (event: unknown): number | null => {
  const e = event as { points?: Array<{ customdata?: unknown }> } | undefined;
  const p = e?.points?.[0];
  const cd = p?.customdata;
  if (
    Array.isArray(cd) &&
    cd.length >= 2 &&
    typeof cd[0] === 'number' &&
    cd[1]
  ) {
    return cd[0];
  }
  return null;
};
